import compact from 'lodash/compact';
import flatMap from 'lodash/flatMap';
import assocPath from 'lodash/fp/assocPath'; // Because it returns a copy
import compose from 'lodash/fp/compose';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import sum from 'lodash/sum';
import { func } from 'prop-types';
import React from 'react';

import { withDocumentTitle } from '../generic/DocumentTitle';
import ShareInput from '../generic/ShareInput';
import { getTooltipButtonWithText } from '../generic/Tooltip';
import Titled from '../layout/Titled';
import { benchmarkRecommendationShape } from '../prop-types';
import api from '../REST/api';
import withApiRequest from '../REST/with-api-request';
import getEventTargetValue from '../utils/get-event-target-value';
import DistributionShareTotal from './DistributionShareTotal';
import DistributionTable from './DistributionTable';
import FilterBenchmarks from './FilterBenchmarks';
import HELP_TEXTS from './help-texts';
import PeerBrands from './PeerBrands';
import TargetKpiBenchmarks from './TargetKpiBenchmarks';

function getProductCountryShares(products, countries, shares) {
  return flatMap(products, product =>
    compact(
      countries.map(country => {
        const share = get(shares, [product.code, country]);
        return share
          ? {
              productCode: product.code,
              countryCode: country,
              share: shares[product.code][country],
            }
          : null;
      }),
    ),
  );
}

const ButtonWithTooltip = getTooltipButtonWithText(HELP_TEXTS);

class Benchmarks extends React.Component {
  state = {
    isDirty: true,
    brand: null,
    peerBrands: [],
    countries: [],
    products: [],
    objective: 'Other',
    pool: 'Other',
    shares: {},
    businessModel: null,
    startDate: null,
    endDate: null,
  };

  handleChange = (event, model) => {
    const { target } = event;
    const { name } = target;
    const value = model || getEventTargetValue(target);
    this.setState(
      state => ({
        ...assocPath(name, value, state),
        isDirty: true,
      }),
      this.cleanUpShares,
    );
  };

  handleCalculate = () => {
    const {
      brand,
      objective,
      pool,
      products,
      countries,
      shares,
      peerBrands,
      businessModel,
    } = this.state;
    this.props
      .onRequest({
        objective,
        brandCode: brand && brand.code,
        peerBrands:
          peerBrands.length > 0 ? peerBrands.map(({ code }) => code) : null,
        pool,
        productCountryShares: getProductCountryShares(
          products,
          countries,
          shares,
        ),
        businessModel,
      })
      .then(() => this.setState({ isDirty: false }));
  };

  getDistributionTotal() {
    const productShares = Object.values(this.state.shares).map(countryShares =>
      sum(Object.values(countryShares)),
    );
    return Math.round(sum(productShares) * 10000) / 100;
  }

  cleanUpShares = () => {
    this.setState(state => {
      const newShares = {};
      const oldShares = state.shares;
      state.products.forEach(product => {
        const pCode = product.code;
        newShares[pCode] = {};
        state.countries.forEach(country => {
          newShares[pCode][country] = get(oldShares, [pCode, country], 0);
        });
      });
      return { shares: newShares };
    });
  };

  hasData() {
    const { brand, countries, products, objective, pool } = this.state;
    return (
      brand && objective && pool && !isEmpty(products) && !isEmpty(countries)
    );
  }

  render() {
    const { response } = this.props;
    const {
      isDirty,
      brand,
      peerBrands,
      pool,
      objective,
      products,
      countries,
      shares,
      businessModel,
      startDate,
      endDate,
    } = this.state;

    const distributionTotal = this.getDistributionTotal();
    const isDisabled = !this.hasData() || distributionTotal !== 100;

    return (
      <Titled
        title="Benchmarks"
        actions={
          <ButtonWithTooltip
            className="calculate"
            helpKey="calculate"
            section="button"
            color="secondary"
            onClick={this.handleCalculate}
            disabled={isDisabled}
            showTooltip={isDisabled}
          >
            Calculate
          </ButtonWithTooltip>
        }
      >
        <FilterBenchmarks
          brand={brand}
          peerBrands={peerBrands}
          pool={pool}
          objective={objective}
          countries={countries}
          products={products}
          businessModel={businessModel}
          startDate={startDate}
          endDate={endDate}
          onChange={this.handleChange}
        />
        <div className="mb-4" />
        {products.length && countries.length ? (
          <Titled
            title="Budget Distribution"
            actions={[<DistributionShareTotal total={distributionTotal} />]}
          >
            <DistributionTable
              products={products}
              countries={countries}
              shares={shares}
              displayComponent={(share, product, country) => (
                <ShareInput
                  name={`shares.${product.code}.${country}`}
                  value={share || 0}
                  onChange={this.handleChange}
                />
              )}
            />
          </Titled>
        ) : null}
        <div className="mb-4" />
        {!isDirty && response && (
          <>
            <TargetKpiBenchmarks
              data={response}
              products={products}
              countries={countries}
            />
            {isEmpty(peerBrands) && (
              <PeerBrands brandCode={this.state.brand.code} />
            )}
            <div className="mb-4" />
          </>
        )}
      </Titled>
    );
  }
}

Benchmarks.propTypes = {
  onRequest: func.isRequired,
  response: benchmarkRecommendationShape,
};

Benchmarks.defaultProps = {
  response: null,
};

export default compose(
  withApiRequest({
    query: api.getKpiRecommendation,
    error: 'flash',
    renderLoading: true,
  }),
  withDocumentTitle('Benchmarks'),
)(Benchmarks);
